/**
 * SynesthesiaGrid - Synesthesia (the VJ app on STREAMPC) as a page (2026-11-05, OPBridge S344/S352;
 * `.notepads/synesthesia-app-half.md`).
 *
 * Top to bottom: a header (the PC it runs on, when a press lands, what was last sent), the scenes as PICTURES (a tap
 * launches one), the moves (Previous / Random / Next / Home), Synesthesia's own playlist (opt-in), the look (its meta
 * controls: a slider or a switch each, as OPBridge lists them) and the playing scene's own controls 1-8 (opt-in).
 *
 * ## What it can honestly show
 *
 * Synesthesia hears MIDI and says nothing back. So no scene is ever ON AIR here: a press is WAITING (amber, "next bar")
 * from the tap until OPBridge answers on the line, then SENT (a white rim, DEC-321 rule 1) as the last scene THIS
 * DEVICE sent. A slider rests where it was last put from here; its number shows only once it has been moved.
 *
 * ## Taps, not touches
 *
 * A scene fires on a TAP (released within a finger's width of where it went down), so the scene area can scroll on a
 * rig with more scenes than fit, and a scroll never launches one. Everything else fires on the press, like the
 * OPVisual Grid. Home needs a second tap when Guard is on (it leaves the show for the home playlist).
 *
 * Studio look (DEC-320): graphite and hairlines, the pictures carry the colour, colour otherwise means only state.
 * Buttons and sliders are the OPVisual Grid's own (`.opv-btn`, `OpvSlider`), so the two visual surfaces read as one.
 */

import { createEffect, createMemo, createSignal, For, onCleanup, onMount, Show } from 'solid-js';
import { editItem, editOwnsGesture, selectedItemId, type LayoutColor, type SynesthesiaGridItem } from '../layout/store.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import { pushToast } from '../ui/toast.ts';
import { OpvSlider } from './OpVisualGrid.tsx';
import {
  ensureSynList, refreshSynList, synAskRow, synCommands, synMeta, synPicture, synScenes, synSummary, synWhere, watchSynList,
} from '../synesthesia/list.ts';
import { fireSynesthesia, noteSynValue, synKey, synSession, type SynFireResult } from '../synesthesia/fire.ts';
import {
  SYN_MOVES, SYN_PLAYLIST, SYN_WHENS, metaLabel, metaRest, takesCommand, type SynAction, type SynWhen,
} from '../synesthesia/model.ts';
import type { FireResult } from '../opbridge/dispatch.ts';
import type { SynesthesiaScene } from '../opbridge/httpClient.ts';

const COLOR_TOKEN: Record<LayoutColor, string> = { cyan: 'idle', magenta: 'incoming', amber: 'armed', green: 'ok' };

/** A tap moves less than this (px) between down and up; more is a scroll. */
const TAP_SLOP = 10;
/** The smallest picture worth drawing (px wide), and the gap between them. */
const MIN_TILE_W = 96;
const GAP = 6;
const NAME_H = 0; // names sit ON the picture, over its scrim

/** How many columns show `n` pictures (16:9) largest in `w` x `h`; when none fits even at the smallest, as many as
 *  fit across (the area scrolls). Pure. */
export function fitColumns(n: number, w: number, h: number): number {
  if (n <= 0 || w <= 0) return 1;
  let best = 0;
  let bestW = 0;
  for (let cols = 1; cols <= n; cols++) {
    const tw = (w - (cols - 1) * GAP) / cols;
    if (tw < MIN_TILE_W) break;
    const rows = Math.ceil(n / cols);
    const th = (tw * 9) / 16 + NAME_H;
    if (rows * th + (rows - 1) * GAP <= h && tw > bestW) { best = cols; bestW = tw; }
  }
  if (best > 0) return best;
  return Math.max(1, Math.floor((w + GAP) / (MIN_TILE_W + GAP)));
}

export function SynesthesiaGrid(props: { item: SynesthesiaGridItem }) {
  onMount(() => { const release = watchSynList(); onCleanup(release); });
  // A computer connecting (or its health arriving) is when the list can first be read.
  createEffect(() => { if (synAskRow()) ensureSynList(); });

  const sum = () => synSummary();
  const ready = () => sum().state === 'ok';
  const when = (): SynWhen => props.item.when ?? 'bar';
  const whenWord = () => (when() === 'bar' ? 'next bar' : when() === 'beat' ? 'next beat' : 'now');
  const scenes = createMemo((): SynesthesiaScene[] => (props.item.scenes === 'own' ? synScenes().filter((s) => s.own) : synScenes()));
  const s = () => synSession();
  const guard = createDiscreteGuard(() => !!props.item.guard);

  // ---- the scene area's size decides the columns ----
  let area: HTMLDivElement | undefined;
  const [areaSize, setAreaSize] = createSignal({ w: 0, h: 0 });
  onMount(() => {
    if (!area || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => { if (area) setAreaSize({ w: area.clientWidth, h: area.clientHeight }); });
    ro.observe(area);
    onCleanup(() => ro.disconnect());
  });
  const cols = () => {
    const set = props.item.cols;
    if (set && set > 0) return Math.round(set);
    const { w, h } = areaSize();
    return fitColumns(scenes().length, w - 2, h - 2);
  };

  function report(r: SynFireResult): void {
    if (r.ok || r.quiet) return;
    pushToast('error', `Synesthesia: ${r.why}`);
  }
  async function send(a: SynAction, at: SynWhen = when()): Promise<SynFireResult> {
    const r = await fireSynesthesia(a, at);
    report(r);
    return r;
  }
  /** The press gate every control shares: not while editing or selected (the wrapper owns the gesture). */
  function mayAct(e: PointerEvent): boolean {
    if (editOwnsGesture(props.item.id)) return false;
    if (selectedItemId() === props.item.id) return false;
    e.stopPropagation();
    if (!ready()) { pushToast('error', `Synesthesia: ${sum().text}`); return false; }
    return true;
  }

  // ---- scenes: fire on a tap ----
  let down: { id: number; x: number; y: number; name: string } | null = null;
  function sceneDown(e: PointerEvent, name: string): void {
    if (editOwnsGesture(props.item.id) || selectedItemId() === props.item.id) return;
    e.stopPropagation();
    down = { id: e.pointerId, x: e.clientX, y: e.clientY, name };
  }
  function sceneUp(e: PointerEvent, sc: SynesthesiaScene): void {
    const d = down;
    down = null;
    if (!d || d.id !== e.pointerId || d.name !== sc.name) return;
    if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > TAP_SLOP) return;
    if (!mayAct(e)) return;
    if (!sc.mapped) {
      pushToast('error', `Synesthesia: "${sc.name}" has no mapping yet. Add it in OPBridge's Synesthesia panel and write the mappings.`);
      return;
    }
    void send({ command: 'scene', scene: sc.name });
  }

  const sceneState = (sc: SynesthesiaScene): 'waiting' | 'sent' | 'unmapped' | '' => {
    if (s().waiting[synKey({ command: 'scene', scene: sc.name })] !== undefined) return 'waiting';
    if (!sc.mapped) return 'unmapped';
    if (s().lastScene?.name === sc.name) return 'sent';
    return '';
  };

  // ---- the header's line: what was last sent ----
  const statusLine = (): string => {
    const waitingScene = Object.keys(s().waiting).find((k) => k.startsWith('scene:'));
    if (waitingScene) return `${waitingScene.slice(6)} on the ${whenWord()}`;
    if (s().lastScene) return `Sent: ${s().lastScene!.name}`;
    if (s().lastMove) return `Sent: ${s().lastMove!.label}`;
    return '';
  };

  // ---- moves and playlist ----
  function move(e: PointerEvent, command: SynAction['command']): void {
    if (!mayAct(e)) return;
    if (command === 'home' && !guard.gate('home')) return;
    void send({ command });
  }
  const moveWaiting = (command: SynAction['command']) => s().waiting[synKey({ command })] !== undefined;
  const takes = (command: SynAction['command']) => takesCommand(command, synCommands());

  // ---- the look ----
  const metaValue = (name: string, type: string): number => s().values[name] ?? metaRest(name, type);
  const metaTouched = (name: string): boolean => s().values[name] !== undefined;
  const sendMeta = (control: string, value: number): Promise<FireResult> =>
    fireSynesthesia({ command: 'meta', control, value }, 'now').then((r) => { report(r); return { target: '', kind: r.ok ? 'ok' : 'http-error' } as FireResult; });
  function toggle(e: PointerEvent, name: string): void {
    if (!mayAct(e)) return;
    const on = metaValue(name, 'toggle') >= 0.5;
    void send({ command: 'meta', control: name, value: on ? 0 : 1 });
  }
  // The playing scene's own controls: where each was last put from here (not persisted; a scene change resets them in
  // Synesthesia anyway).
  const [ctl, setCtl] = createSignal<Array<number | null>>(Array.from({ length: 8 }, () => null));

  const lookSliders = () => synMeta().filter((m) => m.type !== 'toggle');
  const lookToggles = () => synMeta().filter((m) => m.type === 'toggle');

  function setWhen(e: PointerEvent, w: SynWhen): void {
    if (editOwnsGesture(props.item.id) || selectedItemId() === props.item.id) return;
    e.stopPropagation();
    editItem(props.item.id, { when: w });
  }

  return (
    <div
      class="syn-grid"
      data-color={COLOR_TOKEN[props.item.color]}
      data-state={sum().state}
      style={{ '--syn-cols': String(cols()) }}
    >
      {/* ── Header ── */}
      <div class="syn-head">
        <span class="syn-title">{props.item.hideName ? '' : (props.item.name ?? 'Synesthesia')}</span>
        <Show when={synWhere()}>
          <span class="syn-where" title="The PC that drives Synesthesia. A press from here goes there through OPBridge.">on {synWhere()}</span>
        </Show>
        <span class="syn-status" data-testid="syn-status">{statusLine()}</span>
        <span class="opv-spacer" />
        <div class="opv-seg" role="group" aria-label="When a scene lands">
          <For each={SYN_WHENS}>
            {([w, label]) => (
              <button type="button" class="opv-btn" data-when={w} data-selected={when() === w ? 'true' : 'false'}
                onPointerDown={(e) => setWhen(e, w)}
                title={w === 'bar' ? 'Scenes and moves land on the next bar line' : w === 'beat' ? 'Scenes and moves land on the next beat' : 'Scenes and moves land at once'}
              >{label}</button>
            )}
          </For>
        </div>
      </div>

      {/* ── Scenes ── */}
      <div class="syn-scenes" ref={area} data-testid="syn-scenes">
        <Show when={ready()} fallback={
          <div class="syn-msg" data-state={sum().state}>
            <span>{sum().text}</span>
            <Show when={sum().state !== 'reading' && sum().state !== 'nobridge'}>
              <button type="button" class="opv-btn" onPointerDown={(e) => { e.stopPropagation(); void refreshSynList(); }}>Read again</button>
            </Show>
          </div>
        }>
          <Show when={scenes().length > 0} fallback={
            <div class="syn-msg" data-state="empty">
              <span>{props.item.scenes === 'own'
                ? 'None of your own scenes are listed. Show every scene in the Inspector, or add scenes in OPBridge\'s Synesthesia panel.'
                : 'No scenes are listed. Pick playlists in OPBridge\'s Synesthesia panel, on the PC that runs it.'}</span>
            </div>
          }>
            <div class="syn-tiles">
              <For each={scenes()}>
                {(sc) => (
                  <button
                    type="button"
                    class="syn-scene"
                    data-scene={sc.name}
                    data-own={sc.own ? 'true' : 'false'}
                    data-state={sceneState(sc)}
                    data-img={synPicture(sc.name) ? 'true' : 'false'}
                    onPointerDown={(e) => sceneDown(e, sc.name)}
                    onPointerUp={(e) => sceneUp(e, sc)}
                    onPointerCancel={() => { down = null; }}
                    title={sc.mapped ? `${sc.name}: tap to launch it on the ${whenWord()}` : `${sc.name}: not mapped in Synesthesia yet`}
                  >
                    <Show when={synPicture(sc.name)}>
                      {(src) => <img class="syn-scene-img" src={src()} alt="" draggable={false} />}
                    </Show>
                    <Show when={props.item.sceneNames !== false}>
                      <span class="syn-scene-name">{sc.name}</span>
                    </Show>
                    <Show when={sceneState(sc)}>
                      <span class="syn-scene-tag">{sceneState(sc) === 'waiting' ? (when() === 'beat' ? 'NEXT BEAT' : 'NEXT BAR') : sceneState(sc) === 'sent' ? 'SENT' : 'NOT MAPPED'}</span>
                    </Show>
                  </button>
                )}
              </For>
            </div>
          </Show>
        </Show>
      </div>

      {/* ── Moves + playlist ── */}
      <Show when={props.item.showMoves !== false || props.item.showPlaylist}>
        <div class="syn-moves">
          <Show when={props.item.showMoves !== false}>
            <For each={SYN_MOVES.filter((m) => takes(m.command))}>
              {(m) => (
                <button
                  type="button" class="opv-btn syn-move" data-command={m.command}
                  data-tone={m.command === 'home' && props.item.guard ? 'warn' : undefined}
                  data-armed={m.command === 'home' && guard.armedKey() === 'home' ? 'true' : 'false'}
                  data-waiting={moveWaiting(m.command) ? 'true' : 'false'}
                  data-sent={m.command === 'home' && s().lastMove?.label === m.label && !s().lastScene ? 'true' : undefined}
                  disabled={!ready()}
                  onPointerDown={(e) => move(e, m.command)}
                  title={`${m.label}: ${m.hint}, on the ${whenWord()}`}
                >{m.short}</button>
              )}
            </For>
          </Show>
          <Show when={props.item.showPlaylist && SYN_PLAYLIST.some((m) => takes(m.command))}>
            <span class="syn-moves-label">Playlist</span>
            <For each={SYN_PLAYLIST.filter((m) => takes(m.command))}>
              {(m) => (
                <button
                  type="button" class="opv-btn syn-move" data-command={m.command}
                  data-waiting={moveWaiting(m.command) ? 'true' : 'false'}
                  disabled={!ready()}
                  onPointerDown={(e) => move(e, m.command)}
                  title={`${m.label}: ${m.hint}, on the ${whenWord()}`}
                >{m.short}</button>
              )}
            </For>
          </Show>
        </div>
      </Show>

      {/* ── The look ── */}
      <Show when={props.item.showLook !== false}>
        <div class="syn-look" data-testid="syn-look">
          <div class="syn-look-sliders" style={{ '--syn-look-n': String(Math.max(1, lookSliders().length)) }}>
          <For each={lookSliders()}>
            {(m) => (
              <div class="syn-look-slider" data-control={m.name}>
                <OpvSlider
                  label={metaLabel(m.name).toUpperCase()}
                  value={metaValue(m.name, m.type)}
                  touched={metaTouched(m.name)}
                  enabled={ready()}
                  itemId={props.item.id}
                  defaultValue={metaRest(m.name, m.type)}
                  onLocal={(v) => noteSynValue(m.name, v)}
                  send={(v) => sendMeta(m.name, v)}
                  title={`Synesthesia's ${metaLabel(m.name).toLowerCase()}. At once; double-tap puts it back to ${Math.round(metaRest(m.name, m.type) * 100)}.`}
                />
              </div>
            )}
          </For>
          </div>
          <Show when={lookToggles().length > 0}>
            <div class="syn-look-toggles">
              <For each={lookToggles()}>
                {(m) => (
                  <button
                    type="button" class="opv-btn syn-toggle" data-control={m.name}
                    data-selected={metaValue(m.name, 'toggle') >= 0.5 ? 'true' : 'false'}
                    data-waiting={s().waiting[synKey({ command: 'meta', control: m.name })] !== undefined ? 'true' : 'false'}
                    disabled={!ready()}
                    onPointerDown={(e) => toggle(e, m.name)}
                    title={`${metaLabel(m.name)} on or off, on the ${whenWord()}. Lit = on, as last sent from here.`}
                  >{metaLabel(m.name)}</button>
                )}
              </For>
            </div>
          </Show>
        </div>
      </Show>

      {/* ── The playing scene's own controls ── */}
      <Show when={props.item.showSceneControls && takes('control')}>
        <div class="syn-look syn-ctl">
          <div class="syn-look-sliders" style={{ '--syn-look-n': '8' }}>
          <For each={[1, 2, 3, 4, 5, 6, 7, 8]}>
            {(n) => (
              <div class="syn-look-slider" data-slot={n}>
                <OpvSlider
                  label={`C${n}`}
                  value={ctl()[n - 1] ?? 0.5}
                  touched={ctl()[n - 1] !== null}
                  enabled={ready()}
                  itemId={props.item.id}
                  onLocal={(v) => setCtl((cur) => cur.map((x, i) => (i === n - 1 ? v : x)))}
                  send={(v) => fireSynesthesia({ command: 'control', slot: n, value: v }, 'now').then((r) => { report(r); return { target: '', kind: r.ok ? 'ok' : 'http-error' } as FireResult; })}
                  title={`The playing scene's control ${n}, at once. Works once it has been taught in Synesthesia (OPBridge's Synesthesia panel, Teach).`}
                />
              </div>
            )}
          </For>
          </div>
        </div>
      </Show>
    </div>
  );
}
